import React, { useState, useRef, useEffect } from 'react';
import './HorizontalColorPicker.css';

interface HorizontalColorPickerProps {
	onColorChange: (color: string) => void;
	style?: React.CSSProperties;
	selected?: string;
	includeBlackAndWhite?: boolean;
}

interface HSVColor {
	h: number;
	s: number;
	v: number;
}

const HorizontalColorPicker: React.FC<HorizontalColorPickerProps> = ({ onColorChange, style, selected, includeBlackAndWhite = true }) => {
	const [selectedColor, setSelectedColor] = useState('#FF0000');
	const [isDragging, setIsDragging] = useState(false);
	const pickerRef = useRef<HTMLDivElement>(null);
	const indicatorRef = useRef<HTMLDivElement>(null);

	const PICKER_WIDTH = style?.width || 300;
	const INDICATOR_SIZE = 28;

	// Convert hex to HSV
	const convertHexToHSV = (hex: string): HSVColor => {
		// Format the hex value to ensure it has the # prefix
		const formattedHex = hex.charAt(0) === '#' ? hex : `#${hex}`;

		// Special cases for black and white
		if (formattedHex.toLowerCase() === '#ffffff') {
			return { h: 0, s: 0, v: 1 };
		}
		if (formattedHex.toLowerCase() === '#000000') {
			return { h: 0, s: 0, v: 0 };
		}

		// Parse the hex values to RGB
		const r = parseInt(formattedHex.slice(1, 3), 16) / 255;
		const g = parseInt(formattedHex.slice(3, 5), 16) / 255;
		const b = parseInt(formattedHex.slice(5, 7), 16) / 255;

		const max = Math.max(r, g, b);
		const min = Math.min(r, g, b);
		const delta = max - min;

		// Calculate HSV values
		let h = 0;
		if (delta === 0) {
			h = 0;
		} else if (max === r) {
			h = ((g - b) / delta) % 6;
		} else if (max === g) {
			h = (b - r) / delta + 2;
		} else {
			h = (r - g) / delta + 4;
		}

		h = Math.round(h * 60);
		if (h < 0) h += 360;

		const s = max === 0 ? 0 : delta / max;
		const v = max;

		return { h, s, v };
	};

	// Calculate position on the slider from color
	const getPositionFromColor = (color: string): number => {
		const hsv = convertHexToHSV(color);
		const width = PICKER_WIDTH as number;

		if (includeBlackAndWhite) {
			// Special case for black (#000000)
			if (hsv.s === 0 && hsv.v === 0) {
				return 0;
			}

			// Special case for white (#FFFFFF)
			if (hsv.s === 0 && hsv.v === 1) {
				return width;
			}

			// For all other colors, position them in the middle section
			const colorWidth = width * 0.9;
			const offset = width * 0.05;
			return offset + (hsv.h / 360) * colorWidth;
		} else {
			return (hsv.h / 360) * width;
		}
	};

	// HSV to Hex conversion
	const HSVtoHex = (h: number, s: number, v: number): string => {
		// Special cases for black and white
		if (v === 0) return '#000000'; // Black
		if (s === 0 && v === 1) return '#FFFFFF'; // White

		let r: number = 0,
			g: number = 0,
			b: number = 0;
		const i = Math.floor(h / 60);
		const f = h / 60 - i;
		const p = v * (1 - s);
		const q = v * (1 - f * s);
		const t = v * (1 - (1 - f) * s);

		switch (i % 6) {
			case 0:
				r = v;
				g = t;
				b = p;
				break;
			case 1:
				r = q;
				g = v;
				b = p;
				break;
			case 2:
				r = p;
				g = v;
				b = t;
				break;
			case 3:
				r = p;
				g = q;
				b = v;
				break;
			case 4:
				r = t;
				g = p;
				b = v;
				break;
			case 5:
				r = v;
				g = p;
				b = q;
				break;
		}

		const toHex = (x: number): string => {
			const hex = Math.round(x * 255).toString(16);
			return hex.length === 1 ? '0' + hex : hex;
		};

		return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
	};

	// Convert position to color
	const getColorFromPosition = (position: number): string => {
		const width = PICKER_WIDTH as number;
		const normalizedPosition = Math.max(0, Math.min(position, width));

		if (includeBlackAndWhite) {
			const blackThreshold = width * 0.05;
			const whiteThreshold = width * 0.95;

			// If position is near the beginning, return black
			if (normalizedPosition <= blackThreshold) {
				// Linear interpolation for smooth transition to black
				const ratio = normalizedPosition / blackThreshold;
				if (ratio < 0.5) return '#000000';
				// Transition from black to first color
				const hue = 0; // Red
				const value = ratio; // Increasing brightness
				return HSVtoHex(hue, 1, value);
			}

			// If position is near the end, return white
			if (normalizedPosition >= whiteThreshold) {
				// Linear interpolation for smooth transition to white
				const ratio = (normalizedPosition - whiteThreshold) / (width - whiteThreshold);
				if (ratio > 0.5) return '#FFFFFF';
				// Transition from last color to white
				const hue = 300; // Magenta
				const saturation = 1 - ratio; // Decreasing saturation
				return HSVtoHex(hue, saturation, 1);
			}

			// For middle positions, calculate the hue
			const colorWidth = width * 0.9;
			const offset = width * 0.05;
			const adjustedPosition = normalizedPosition - offset;
			const hue = (adjustedPosition / colorWidth) * 360;
			return HSVtoHex(hue, 1, 1);
		} else {
			const hue = (normalizedPosition / width) * 360;
			return HSVtoHex(hue, 1, 1);
		}
	};

	// Update the picker position and color when 'selected' prop changes, but only if not dragging
	useEffect(() => {
		if (selected && !isDragging) {
			const position = getPositionFromColor(selected);
			setSelectedColor(selected);

			if (indicatorRef.current) {
				// Ensure indicator is properly positioned by also accounting for its size
				const adjustedPosition = position - INDICATOR_SIZE / 2;
				indicatorRef.current.style.left = `${adjustedPosition}px`;
			}
		}
	}, [selected, PICKER_WIDTH, includeBlackAndWhite, isDragging]);

	// Handle mouse down on the picker
	const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
		e.preventDefault();
		setIsDragging(true);

		if (!pickerRef.current) return;

		const rect = pickerRef.current.getBoundingClientRect();
		const x = e.clientX - rect.left;
		updateColorFromPosition(x);

		const handleMouseMove = (moveEvent: MouseEvent): void => {
			moveEvent.preventDefault();
			if (!pickerRef.current) return;

			const rect = pickerRef.current.getBoundingClientRect();
			const x = moveEvent.clientX - rect.left;
			updateColorFromPosition(x);
		};

		const handleMouseUp = (): void => {
			setIsDragging(false);
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	};

	const updateColorFromPosition = (x: number): void => {
		const width = PICKER_WIDTH as number;

		// Constrain position within picker bounds
		const position = Math.max(0, Math.min(x, width));

		// Calculate color based on position
		const newColor = getColorFromPosition(position);

		// Update state
		setSelectedColor(newColor);

		// Update indicator position - accounting for the indicator size
		if (indicatorRef.current) {
			const adjustedPosition = position - INDICATOR_SIZE / 2;
			indicatorRef.current.style.left = `${adjustedPosition}px`;
		}

		// Call the callback
		if (onColorChange) {
			onColorChange(newColor);
		}
	};

	// Initial setup effect
	useEffect(() => {
		if (selected) {
			const position = getPositionFromColor(selected);
			setSelectedColor(selected);

			if (indicatorRef.current) {
				// Account for indicator size
				const adjustedPosition = position - INDICATOR_SIZE / 2;
				indicatorRef.current.style.left = `${adjustedPosition}px`;
			}
		}
	}, []);

	return (
		<div
			className="horizontal-color-picker"
			style={{
				...(style as React.CSSProperties),
				width: PICKER_WIDTH,
			}}
		>
			<div ref={pickerRef} className="gradient-container" onMouseDown={handleMouseDown} style={{ width: PICKER_WIDTH }}>
				<div className={includeBlackAndWhite ? 'gradient-bar-with-bw' : 'gradient-bar'} />
				<div
					ref={indicatorRef}
					className="indicator"
					style={{
						backgroundColor: selectedColor,
						width: INDICATOR_SIZE,
						height: INDICATOR_SIZE,
						transform: 'translateY(-50%)', // Center vertically
					}}
				></div>
			</div>
		</div>
	);
};

export default HorizontalColorPicker;
